
import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Upload, Trash2, Image as ImageIcon, Palette } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ColorSuggestionDialog } from './ColorSuggestionDialog';
import { extractColorsFromImage, ExtractedColors } from '@/utils/colorExtractor';

interface LogoUploadProps {
  currentLogoUrl?: string | null;
  onLogoUpload: (logoUrl: string) => void;
  onLogoRemove: () => void;
  onColorsExtracted?: (colors: ExtractedColors) => void;
}

export const LogoUpload: React.FC<LogoUploadProps> = ({
  currentLogoUrl,
  onLogoUpload,
  onLogoRemove,
  onColorsExtracted
}) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [extractingColors, setExtractingColors] = useState(false);
  const [showColorDialog, setShowColorDialog] = useState(false);
  const [extractedColors, setExtractedColors] = useState<ExtractedColors | null>(null);

  const uploadLogo = async (file: File) => {
    try {
      setUploading(true);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `logo-${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('restaurant-assets')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('restaurant-assets')
        .getPublicUrl(filePath);

      const logoUrl = data.publicUrl;
      onLogoUpload(logoUrl);

      // Extract colors from the uploaded logo
      if (onColorsExtracted) {
        setExtractingColors(true);
        try {
          const colors = await extractColorsFromImage(logoUrl);
          setExtractedColors(colors);
          setShowColorDialog(true);
        } catch (colorError) {
          console.error('Color extraction failed:', colorError);
          toast({
            title: 'Color Extraction Failed',
            description: 'Logo uploaded successfully, but color extraction failed.',
            variant: 'destructive',
          });
        } finally {
          setExtractingColors(false);
        }
      }

      toast({
        title: 'Success',
        description: 'Logo uploaded successfully!',
      });
    } catch (error: any) {
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload logo',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      uploadLogo(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    maxFiles: 1,
    disabled: uploading
  });

  const handleApplyColors = (colors: ExtractedColors) => {
    if (onColorsExtracted) {
      onColorsExtracted(colors);
    }
    toast({
      title: 'Colors Applied',
      description: 'Menu colors have been updated to match your logo!',
    });
  };

  const handleDiscardColors = () => {
    toast({
      title: 'Colors Discarded',
      description: 'Logo uploaded without applying color suggestions.',
    });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Restaurant Logo
          </CardTitle>
          <CardDescription>
            Upload your restaurant logo. We'll automatically suggest matching colors for your menu.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {currentLogoUrl ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center w-32 h-32 mx-auto border rounded-lg overflow-hidden bg-gray-50">
                <img
                  src={currentLogoUrl}
                  alt="Current logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" onClick={onLogoRemove}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove Logo
                </Button>
                {onColorsExtracted && (
                  <Button 
                    variant="outline" 
                    onClick={async () => {
                      setExtractingColors(true);
                      try {
                        const colors = await extractColorsFromImage(currentLogoUrl);
                        setExtractedColors(colors);
                        setShowColorDialog(true);
                      } catch (error) {
                        toast({
                          title: 'Color Extraction Failed',
                          description: 'Unable to extract colors from the current logo.',
                          variant: 'destructive',
                        });
                      } finally {
                        setExtractingColors(false);
                      }
                    }}
                    disabled={extractingColors}
                  >
                    <Palette className="h-4 w-4 mr-2" />
                    {extractingColors ? 'Extracting...' : 'Extract Colors'}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input {...getInputProps()} />
              <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium mb-2">
                {uploading ? 'Uploading...' : 'Upload Logo'}
              </p>
              <p className="text-sm text-gray-600">
                {isDragActive
                  ? 'Drop the logo here'
                  : 'Drag & drop your logo here, or click to select'}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Supports PNG, JPG, JPEG, GIF, WebP
              </p>
              {extractingColors && (
                <p className="text-xs text-blue-600 mt-2 flex items-center justify-center gap-1">
                  <Palette className="h-3 w-3" />
                  Extracting colors...
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <ColorSuggestionDialog
        isOpen={showColorDialog}
        onClose={() => setShowColorDialog(false)}
        extractedColors={extractedColors}
        onApply={handleApplyColors}
        onDiscard={handleDiscardColors}
        loading={extractingColors}
      />
    </>
  );
};
